import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ResponseFromServer, CommonDataModel } from '../../_models';
import { IHttpClient, HttpOptions } from '../http-client';
import { IServerHttpClient } from './server-http-client.interface';

export abstract class ResponseUnwrappingHttpClient implements IServerHttpClient {
  protected baseUrl: string;

  constructor(
    protected httpClient: IHttpClient,
    baseUrl: string
  ) {
    this.baseUrl = baseUrl;
  }

  get<TResponse>(relativePath: string, options?: HttpOptions): Observable<TResponse> {
    return this.getRaw<TResponse>(relativePath, options)
      .pipe(map(response => response?.data as TResponse));
  }

  post<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<TResponse> {
    return this.postRaw<TResponse, TBody>(relativePath, body, options)
      .pipe(map(response => response?.data as TResponse));
  }

  put<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<TResponse> {
    return this.putRaw<TResponse, TBody>(relativePath, body, options)
      .pipe(map(response => response?.data as TResponse));
  }

  delete<TResponse>(relativePath: string, options?: HttpOptions): Observable<TResponse> {
    return this.deleteRaw<TResponse>(relativePath, options)
      .pipe(map(response => response?.data as TResponse));
  }

  patch<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<TResponse> {
    return this.patchRaw<TResponse, TBody>(relativePath, body, options)
      .pipe(map(response => response?.data as TResponse));
  }

  getRaw<TResponse>(relativePath: string, options?: HttpOptions): Observable<ResponseFromServer<TResponse>> {
    return this.httpClient
      .get<ResponseFromServer<TResponse>>(this.buildAbsoluteUrl(relativePath), options);
  }

  postRaw<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<ResponseFromServer<TResponse>> {
    return this.httpClient
      .post<ResponseFromServer<TResponse>, TBody>(this.buildAbsoluteUrl(relativePath), body, options);
  }

  putRaw<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<ResponseFromServer<TResponse>> {
    return this.httpClient
      .put<ResponseFromServer<TResponse>, TBody>(this.buildAbsoluteUrl(relativePath), body, options);
  }

  deleteRaw<TResponse>(relativePath: string, options?: HttpOptions): Observable<ResponseFromServer<TResponse>> {
    return this.httpClient
      .delete<ResponseFromServer<TResponse>>(this.buildAbsoluteUrl(relativePath), options);
  }

  patchRaw<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<ResponseFromServer<TResponse>> {
    return this.httpClient
      .patch<ResponseFromServer<TResponse>, TBody>(this.buildAbsoluteUrl(relativePath), body, options);
  }

  abstract getExpanded<TResponse>(relativePath: string, options?: HttpOptions): Observable<CommonDataModel<TResponse>>;

  abstract postExpanded<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<CommonDataModel<TResponse>>;

  protected buildAbsoluteUrl(relativePath: string): string {
    return `${this.baseUrl}/${relativePath}`;
  }
}
